import { Injectable } from "@angular/core";
import { RpKeyboardStateService } from "./keyboard-state.service";
import { getActiveEditable, getFocusedScroller, ListenerGroup, onKeyboardViewportChange } from "./shared/keyboard-viewport.util";

const OVERLAP_VAR = '--rp-kb-overlap';
const FILL_VAR = '--rp-kb-fill';
const TABLET_CLASS = 'rp-tablet-keyboard';

// Frozen tablet dialogs keep their scrollers extending behind the keyboard, so the scroll range is
// short by exactly that overlap and content at the end can never reach the visible band. Measures the
// hidden part of the focused scroller's viewport and exposes it as a var the padding rules consume.
@Injectable({ providedIn: 'root' })
export class RpKeyboardScrollRoomService {

    private listeners = new ListenerGroup();
    private paddedScrollers = new Map<HTMLElement, { room: number; clientHeight: number }>();

    constructor(private readonly keyboardState: RpKeyboardStateService) {
    }

    start(): void {
        if (this.listeners.active || typeof window === 'undefined' || !window.visualViewport) {
            return;
        }
        onKeyboardViewportChange(this.listeners, () => this.update());
    }

    private update(): void {
        if (!this.keyboardState.isKeyboardOpen) {
            this.clear();
            return;
        }
        const active = getActiveEditable();
        if (!active) {
            return;
        }
        const focused = getFocusedScroller(active);
        const scroller = (focused === document.scrollingElement || focused === document.documentElement)
            ? this.findPaddableScroller(active)
            : focused;
        if (!scroller) {
            this.clear();
            return;
        }
        // the var only inherits DOWNWARD, but the padded surface can sit ABOVE the measured scroller (the
        // focus resolves to an inner scrollable like a grid viewport while the footer lives in the outer
        // container) — so every scrollable ancestor gets the var too, each sized from ITS OWN hidden band
        const chain = this.scrollableChain(scroller);
        this.paddedScrollers.forEach((entry, el) => {
            if (chain.indexOf(el) === -1) {
                el.style.removeProperty(OVERLAP_VAR);
                el.style.removeProperty(FILL_VAR);
                this.paddedScrollers.delete(el);
            }
        });
        // never shrink while the same scroller stays focused: after scrolling to the end the content sits
        // above the keyboard only BECAUSE of the buffer — recomputing would drop it and snap the scroll back
        chain.forEach(el => {
            const previous = this.paddedScrollers.get(el);
            const resized = !!previous && previous.clientHeight !== el.clientHeight;
            const stale = !previous || (resized && !this.isTablet());
            const room = stale ? this.neededScrollRoom(el) : Math.max(this.neededScrollRoom(el), previous.room);
            this.paddedScrollers.set(el, { room, clientHeight: el.clientHeight });
            el.style.setProperty(OVERLAP_VAR, `${room}px`);
            this.applyContentFill(el);
        });
    }

    private clear(): void {
        this.paddedScrollers.forEach((entry, el) => {
            el.style.removeProperty(OVERLAP_VAR);
            el.style.removeProperty(FILL_VAR);
        });
        this.paddedScrollers.clear();
    }

    // the opposite case: content ending above the keyboard leaves the box empty below it, and an in-flow
    // footer floats there. This is the distance from the content end down to the keyboard.
    private applyContentFill(scroller: HTMLElement): void {
        scroller.style.setProperty(FILL_VAR, '0px');
        const children = Array.from(scroller.children) as HTMLElement[];
        const stack = children.reduce((total, child) => total + child.offsetHeight, 0);
        const fill = Math.round(window.visualViewport.height - scroller.getBoundingClientRect().top - stack);
        if (fill > 0) {
            scroller.style.setProperty(FILL_VAR, `${fill}px`);
        } else {
            scroller.style.removeProperty(FILL_VAR);
        }
    }

    private isTablet(): boolean {
        return document.documentElement.classList.contains(TABLET_CLASS);
    }

    private scrollableChain(scroller: HTMLElement): HTMLElement[] {
        const chain = [scroller];
        let node = scroller.parentElement;
        while (node && node !== document.body) {
            const overflowY = window.getComputedStyle(node).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                chain.push(node);
            }
            node = node.parentElement;
        }
        return chain;
    }

    // buffer = the hidden part of the scroller's viewport, but only while some content actually sits
    // behind the keyboard — with everything visible a buffer would just add pointless empty scroll and
    // make the content-end footer look detached
    private neededScrollRoom(scroller: HTMLElement): number {
        const keyboardTop = window.visualViewport.height;
        const overlap = Math.round(scroller.getBoundingClientRect().bottom - keyboardTop);
        if (overlap <= 0) {
            return 0;
        }
        const children = Array.from(scroller.children) as HTMLElement[];
        const contentBottom = children.reduce((bottom, child) => Math.max(bottom, child.getBoundingClientRect().bottom), 0);
        return contentBottom > keyboardTop ? overlap : 0;
    }

    // fallback when nothing scrolls yet: nearest overflow-y auto/scroll ancestor WITHOUT requiring
    // overflow — with a short form the buffer itself is what first makes the scroller scrollable.
    // Only used when getFocusedScroller fell through to the document, so an inner auto-overflow
    // wrapper can't shadow the real scroller.
    private findPaddableScroller(el: HTMLElement): HTMLElement | null {
        let node = el.parentElement;
        while (node) {
            const overflowY = window.getComputedStyle(node).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                return node;
            }
            node = node.parentElement;
        }
        return null;
    }
}
